import React from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import SimOpsAPI, { getSimOpsErrorMessage } from 'api/ops/v2/SimOpsAPI'
import MenuClose from 'assets/icons/MenuClose.png'
import { PackAction, PackAllocation } from 'types/api/ops/v2/simOps.type'

import {
  refetchUserAllocations,
  refetchUserEsimQueries
} from '../simOpsQueries'

const ACTION_COPY: Record<
  PackAction,
  { title: string; description: string; success: string; error: string }
> = {
  replay: {
    title: 'Replay Pack Assignment',
    description:
      'Retries assigning this stuck/failed pack to the customer. Only use this when the pack does not have an active plan yet.',
    success: 'Pack assignment replayed successfully',
    error: 'Failed to replay pack assignment'
  },
  terminate: {
    title: 'Terminate Pack',
    description:
      'Terminates the plan on this pack with the vendor and moves the pack back to PENDING. The pack will not be reassigned.',
    success: 'Pack terminated successfully',
    error: 'Failed to terminate pack'
  },
  reset: {
    title: 'Reset Pack',
    description:
      'Terminates the current plan on this pack and assigns it to the customer again in one step.',
    success: 'Pack reset successfully',
    error: 'Failed to reset pack'
  }
}

interface PackActionDialogProps {
  userId: string
  action: PackAction | null
  pack: PackAllocation | null
  handleClose: () => void
  onActionSuccess?: () => void
}

function PackActionDialog({
  userId,
  action,
  pack,
  handleClose,
  onActionSuccess
}: PackActionDialogProps) {
  const queryClient = useQueryClient()
  const copy = action ? ACTION_COPY[action] : null

  const { mutate, isLoading } = useMutation(SimOpsAPI.packAction, {
    onSuccess: (res) => {
      toast.success(
        `${copy?.success}${
          res.data?.data?.status ? ` (${res.data.data.status})` : ''
        }`
      )
      refetchUserAllocations(queryClient, userId)
      refetchUserEsimQueries(queryClient, userId)
      onActionSuccess?.()
    },
    onError: (error) => {
      toast.error(
        getSimOpsErrorMessage(error, copy?.error ?? 'Pack action failed')
      )
    },
    onSettled: () => {
      handleClose()
    }
  })

  const handleConfirm = () => {
    if (!pack || !action) {
      toast.error('No pack selected')
      return
    }
    mutate({ packAssignmentId: pack.id, action })
  }

  return (
    <Dialog
      open={!!action && !!pack}
      maxWidth='sm'
      fullWidth
      onClose={handleClose}
      sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
    >
      <DialogTitle
        sx={{
          fontWeight: '700',
          fontSize: '20px',
          color: '#333',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        {copy?.title}
        <IconButton
          onClick={handleClose}
          size='small'
          sx={{ marginRight: '-8px', marginTop: '-8px' }}
        >
          <img
            style={{ width: '20px', height: '20px' }}
            src={MenuClose}
            alt='Close'
          />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
          {pack?.catalogName || pack?.catalog_name} · Pack assignment ID: {pack?.id}
        </Typography>
        <Typography variant='body1' sx={{ color: '#555', my: 2 }}>
          {copy?.description}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ padding: '16px' }}>
        <LoadingButton
          variant='text'
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancel
        </LoadingButton>
        <LoadingButton
          variant='contained'
          color={action === 'terminate' ? 'error' : 'primary'}
          onClick={handleConfirm}
          loading={isLoading}
          loadingIndicator={<CircularProgress size={24} />}
          sx={{ paddingX: 4, fontWeight: 'bold' }}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default PackActionDialog
